import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { useMemo, useState } from "react";
import Select from "react-select";
import { Country, State, City } from "country-state-city";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { PasswordInput } from "components/ui/password-input";
import { Label } from "components/ui/label";
import { Checkbox } from "components/ui/checkbox";
import { Info, Loader2 } from "lucide-react";
import { useDispatch } from "react-redux";
import { SIGNUP_TYPE_VALUES, signUpPathFor } from "pages/signup/signupTypes";
import { SetAuthUserData } from "store/reducer";
import { apiURl } from "store/actions";
import { api, extractAuthPayload, isSuccessResponse } from "lib/api";
import swtAlert from "lib/sweetAlert";
import {
  required,
  validateEmail,
  validatePassword,
  validateConfirmPassword,
  validatePhone,
  validatePostalCode,
  validateName,
  validateText,
  validateChecked,
  validateForm as runFormValidation,
} from "lib/validation";


/** Mirrors the body documented for POST /api/auth/signup in the Postman
 *  collection (residentialAddress / registeredAddress are derived from these
 *  flat fields at submit time). */
const initialFormData = {
  email: "",
  password: "",
  confirmPassword: "",
  phone: "",
  firstName: "",
  lastName: "",
  companyName: "",
  isOver19Confirmed: false,
  line1: "",
  city: "",
  province: "",
  postalCode: "",
  country: "",
  beneficialOwners: ["", "", "", "", ""],
};

const formRowClass = "grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4 items-start";

/** Stable keys for the fixed-length beneficial-owner inputs (avoids using
 *  array indexes as React keys). */
const BENEFICIAL_OWNER_KEYS = [
  "owner-1",
  "owner-2",
  "owner-3",
  "owner-4",
  "owner-5",
];

const optionStateClass = (isSelected, isFocused) => {
  if (isSelected) return "!bg-accent !text-accent-foreground";
  if (isFocused) return "!bg-accent/40";
  return "!bg-transparent";
};

const reactSelectClassNames = {
  control: ({ isFocused }) =>
    [
      "!min-h-10 !rounded-md !border !bg-background !text-sm",
      isFocused ? "!border-ring !ring-1 !ring-ring" : "!border-input",
    ].join(" "),
  valueContainer: () => "!px-3",
  placeholder: () => "!text-muted-foreground",
  menu: () =>
    "!mt-1 !rounded-md !border !border-input !bg-popover !text-sm !shadow-md !z-50",
  option: ({ isFocused, isSelected }) =>
    `!px-3 !py-2 !cursor-pointer ${optionStateClass(isSelected, isFocused)}`,
  indicatorSeparator: () => "!hidden",
};

/* eslint-disable react/prop-types -- this file is plain JS; helpers below are
 *  used only inside this module and don't need runtime prop validation. */

/** Tiny helper that collapses the repeated `{errors.X && <p>...</p>}` pattern
 *  used throughout the form. Rendering `null` keeps the parent component's
 *  cognitive complexity down. */
function FieldError({ message }) {
  if (!message) return null;
  return <p className="text-xs text-destructive mt-1">{message}</p>;
}

function BeneficialOwners({ values, onChange }) {
  return values.map((owner, idx) => (
    <div key={BENEFICIAL_OWNER_KEYS[idx]} className={formRowClass}>
      <Label
        htmlFor={`beneficialOwner-${idx}`}
        className="md:text-right md:pt-3"
      >
        Beneficial Owner {idx + 1}
      </Label>
      <Input
        id={`beneficialOwner-${idx}`}
        placeholder={`Beneficial Owner ${idx + 1}`}
        value={owner}
        onChange={(e) => onChange(idx, e.target.value)}
      />
    </div>
  ));
}
/* eslint-enable react/prop-types */

export default function SignUpFormPage() {
  const { type } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});

  const countryOptions = useMemo(
    () =>
      Country.getAllCountries().map((c) => ({
        value: c.name,
        label: c.name,
      })),
    []
  );

  const selectedCountryIso = useMemo(() => {
    if (!formData.country) return "";
    return (
      Country.getAllCountries().find((c) => c.name === formData.country)
        ?.isoCode ?? ""
    );
  }, [formData.country]);

  const provinceOptions = useMemo(
    () =>
      selectedCountryIso
        ? State.getStatesOfCountry(selectedCountryIso).map((s) => ({
            value: s.name,
            label: s.name,
          }))
        : [],
    [selectedCountryIso]
  );

  const selectedProvinceIso = useMemo(() => {
    if (!selectedCountryIso || !formData.province) return "";
    return (
      State.getStatesOfCountry(selectedCountryIso).find(
        (s) => s.name === formData.province
      )?.isoCode ?? ""
    );
  }, [selectedCountryIso, formData.province]);

  const cityOptions = useMemo(
    () =>
      selectedCountryIso && selectedProvinceIso
        ? City.getCitiesOfState(selectedCountryIso, selectedProvinceIso).map(
            (c) => ({ value: c.name, label: c.name })
          )
        : [],
    [selectedCountryIso, selectedProvinceIso]
  );

  if (!SIGNUP_TYPE_VALUES.includes(type)) {
    return <Navigate to="/sign-up" replace />;
  }

  const isCorporate = type === "corporate";

  const clearError = (field) => {
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const normalizeFieldValue = (name, value) => {
    if (name === "postalCode") return value.replaceAll(/\s+/g, "").toUpperCase();
    if (name === "phone") return value.replaceAll(/\D+/g, "");
    return value;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: normalizeFieldValue(name, value) }));
    clearError(name);
  };

  const handleBeneficialOwnerChange = (index, value) => {
    setFormData((prev) => {
      const next = [...prev.beneficialOwners];
      next[index] = value;
      return { ...prev, beneficialOwners: next };
    });
  };

  const validateForm = () => {
    const schema = {
      firstName: (v) => validateName(v, { label: "First name" }),
      lastName: (v) => validateName(v, { label: "Last name" }),
      email: (v) => validateEmail(v),
      phone: (v) => validatePhone(v),
      country: required("Country"),
      province: required("Province/State"),
      city: required("City"),
      line1: (v) => validateText(v, { label: "Address", min: 3, max: 120 }),
      postalCode: (v) => validatePostalCode(v, selectedCountryIso),
      password: (v) => validatePassword(v),
      confirmPassword: (v) => validateConfirmPassword(v, formData.password),
      isOver19Confirmed: (v) =>
        validateChecked(v, {
          message: "You must confirm you are over 18 years old",
        }),
    };

    if (isCorporate) {
      schema.companyName = (v) =>
        validateText(v, { label: "Company name", min: 2, max: 120 });
    }

    const newErrors = runFormValidation(formData, schema);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = () => {
    const address = {
      line1: formData.line1.trim(),
      city: formData.city,
      province: formData.province,
      postalCode: formData.postalCode.trim(),
      country: formData.country,
    };

    const payload = {
      investorType: type,
      email: formData.email.trim(),
      password: formData.password,
      confirmPassword: formData.confirmPassword,
      phone: formData.phone.trim(),
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      isOver19Confirmed: formData.isOver19Confirmed,
      [isCorporate ? "registeredAddress" : "residentialAddress"]: address,
      status:"inactive"
    };

    if (isCorporate) {
      payload.companyName = formData.companyName.trim();
      payload.beneficialOwners = formData.beneficialOwners
        .map((o) => o.trim())
        .filter(Boolean);
    }

    return payload;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      swtAlert.error(
        "Validation Error",
        "Please fill in all required fields correctly"
      );
      return;
    }

    setIsLoading(true);
    try {
      const resp = await api.post(apiURl.signup, buildPayload(), {
        auth: false,
      });

      if (!isSuccessResponse(resp)) {
        swtAlert.error(
          "Signup Failed",
          resp?.message || "Signup failed. Please try again."
        );
        return;
      }

      const auth = extractAuthPayload(resp);
      dispatch(SetAuthUserData(auth));

      swtAlert.success(
        "Account Created",
        resp?.message ||
          (isCorporate
            ? "Corporate account created successfully!"
            : "Account created successfully!")
      );
      // If the backend issued a session immediately, jump to the dashboard.
      // Otherwise (e.g. email-verification flow), send them to sign-in.
      navigate(auth.token ? "/" : "/sign-in");
    } catch (err) {
      swtAlert.error(
        "Signup Failed",
        err?.message || "Signup failed. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const otherTypePath = signUpPathFor(isCorporate ? "individual" : "corporate");
  const otherTypeLabel = isCorporate ? "Individual Registration" : "Corporate Registration";
  const title = isCorporate ? "Corporate Registration" : "Individual Investor Registration";

  const selectedCountry =
    countryOptions.find((o) => o.value === formData.country) || null;
  const selectedProvince =
    provinceOptions.find((o) => o.value === formData.province) || null;
  const selectedCity =
    cityOptions.find((o) => o.value === formData.city) || null;
  return (
    <div className="container mx-auto max-w-3xl px-4 py-12 sm:px-6 lg:px-12 xl:px-16">
      <h1 className="text-2xl font-semibold text-center mb-6">{title}</h1>

      {isCorporate && (
        <div className="flex items-start gap-2 text-sm text-muted-foreground mb-8 justify-center">
          <Info className="h-4 w-4 mt-0.5 shrink-0" />
          <span>
            Fill out this form to make investments as a corporation. To invest as an individual,
            fill out the{" "}
            <Link to={signUpPathFor("individual")} className="text-accent hover:underline">
              individual registration form
            </Link>
            .
          </span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {isCorporate && (
          <div className={formRowClass}>
            <Label htmlFor="companyName" className="md:text-right md:pt-3">
              Company Name
            </Label>
            <div>
              <Input
                id="companyName"
                name="companyName"
                placeholder="Company Name"
                value={formData.companyName}
                onChange={handleInputChange}
                aria-invalid={!!errors.companyName}
              />
              <FieldError message={errors.companyName} />
            </div>
          </div>
        )}

        {/* First Name */}
        <div className={formRowClass}>
          <Label htmlFor="firstName" className="md:text-right md:pt-3">
            First Name
          </Label>
          <div>
            <Input
              id="firstName"
              name="firstName"
              placeholder="First Name"
              value={formData.firstName}
              onChange={handleInputChange}
              aria-invalid={!!errors.firstName}
            />
            <FieldError message={errors.firstName} />
          </div>
        </div>

        {/* Last Name */}
        <div className={formRowClass}>
          <Label htmlFor="lastName" className="md:text-right md:pt-3">
            Last Name
          </Label>
          <div>
            <Input
              id="lastName"
              name="lastName"
              placeholder="Last Name"
              value={formData.lastName}
              onChange={handleInputChange}
              aria-invalid={!!errors.lastName}
            />
            <FieldError message={errors.lastName} />
          </div>
        </div>

        {/* Email */}
        <div className={formRowClass}>
          <Label htmlFor="email" className="md:text-right md:pt-3">
            Email
          </Label>
          <div>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleInputChange}
              aria-invalid={!!errors.email}
            />
            <FieldError message={errors.email} />
          </div>
        </div>

        {/* Phone Number */}
        <div className={formRowClass}>
          <Label htmlFor="phone" className="md:text-right md:pt-3">
            Phone Number
          </Label>
          <div>
            <Input
              id="phone"
              name="phone"
              type="tel"
              inputMode="numeric"
              autoComplete="tel"
              pattern="[0-9]*"
              placeholder="Phone Number: 6042224444"
              value={formData.phone}
              onChange={handleInputChange}
              aria-invalid={!!errors.phone}
            />
            <FieldError message={errors.phone} />
          </div>
        </div>

        {/* Country */}
        <div className={formRowClass}>
          <Label className="md:text-right md:pt-3">Country</Label>
          <div>
            <Select
              inputId="country"
              options={countryOptions}
              value={selectedCountry}
              onChange={(opt) => {
                setFormData((prev) => ({
                  ...prev,
                  country: opt?.value || "",
                  province: "",
                  city: "",
                }));
                clearError("country");
              }}
              placeholder="Select Country"
              isClearable
              classNames={reactSelectClassNames}
            />
            <FieldError message={errors.country} />
          </div>
        </div>

        {/* Province / State */}
        <div className={formRowClass}>
          <Label className="md:text-right md:pt-3">Province/State</Label>
          <div>
            <Select
              inputId="province"
              options={provinceOptions}
              value={selectedProvince}
              onChange={(opt) => {
                setFormData((prev) => ({
                  ...prev,
                  province: opt?.value || "",
                  city: "",
                }));
                clearError("province");
              }}
              placeholder={
                formData.country ? "Select Province/State" : "Select country first"
              }
              isDisabled={!formData.country}
              isClearable
              classNames={reactSelectClassNames}
            />
            <FieldError message={errors.province} />
          </div>
        </div>

        {/* City */}
        <div className={formRowClass}>
          <Label className="md:text-right md:pt-3">City</Label>
          <div>
            <Select
              inputId="city"
              options={cityOptions}
              value={selectedCity}
              onChange={(opt) => {
                setFormData((prev) => ({ ...prev, city: opt?.value || "" }));
                clearError("city");
              }}
              placeholder={
                formData.province ? "Select City" : "Select province/state first"
              }
              isDisabled={!formData.province}
              isClearable
              classNames={reactSelectClassNames}
            />
            <FieldError message={errors.city} />
          </div>
        </div>

        {/* Address line */}
        <div className={formRowClass}>
          <Label htmlFor="line1" className="md:text-right md:pt-3">
            Address
          </Label>
          <div>
            <Input
              id="line1"
              name="line1"
              placeholder="Street address"
              value={formData.line1}
              onChange={handleInputChange}
              aria-invalid={!!errors.line1}
            />
            <FieldError message={errors.line1} />
          </div>
        </div>

        {/* Postal Code */}
        <div className={formRowClass}>
          <Label htmlFor="postalCode" className="md:text-right md:pt-3">
            Postal Code
          </Label>
          <div>
            <Input
              id="postalCode"
              name="postalCode"
              placeholder="Postal Code"
              value={formData.postalCode}
              onChange={handleInputChange}
              aria-invalid={!!errors.postalCode}
            />
            <FieldError message={errors.postalCode} />
          </div>
        </div>

        <div className={formRowClass}>
          <Label htmlFor="password" className="md:text-right md:pt-3">
            Password
          </Label>
          <div>
            <PasswordInput
              id="password"
              name="password"
              placeholder="Password"
              value={formData.password}
              onChange={handleInputChange}
              aria-invalid={!!errors.password}
            />
            <FieldError message={errors.password} />
            {!errors.password && (
              <p className="text-xs text-muted-foreground mt-1">
                Min. 8 characters with uppercase, lowercase, number & special character.
              </p>
            )}
          </div>
        </div>

        <div className={formRowClass}>
          <Label htmlFor="confirmPassword" className="md:text-right md:pt-3">
            Confirm Password
          </Label>
          <div>
            <PasswordInput
              id="confirmPassword"
              name="confirmPassword"
              placeholder="Confirm Password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              aria-invalid={!!errors.confirmPassword}
            />
            <FieldError message={errors.confirmPassword} />
          </div>
        </div>

        {isCorporate && (
          <BeneficialOwners
            values={formData.beneficialOwners}
            onChange={handleBeneficialOwnerChange}
          />
        )}

        {/* Age Confirmation */}
        <div className={formRowClass}>
          <Label className="md:text-right md:pt-1 leading-5">
            I confirm that I am over 18 years old.
          </Label>
          <div>
            <Checkbox
              id="isOver19Confirmed"
              checked={formData.isOver19Confirmed}
              onCheckedChange={(checked) => {
                setFormData((prev) => ({ ...prev, isOver19Confirmed: !!checked }));
                clearError("isOver19Confirmed");
              }}
              aria-invalid={!!errors.isOver19Confirmed}
            />
            <FieldError message={errors.isOver19Confirmed} />
          </div>
        </div>

        {/* Submit Button */}
        <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4 pt-4">
          <div></div>
          <Button type="submit" disabled={isLoading} className="w-full md:w-auto">
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </div>
      </form>

      <p className="text-center text-sm text-muted-foreground mt-8">
        Want to register as {isCorporate ? "an individual" : "a corporation"}?{" "}
        <Link to={otherTypePath} className="text-accent hover:underline font-medium">
          {otherTypeLabel}
        </Link>
      </p>
    </div>
  );
}
